import { useEffect, useRef } from 'react';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { setCurrentTab } from '../../../redux/slices/visitsSlice';
import { useDispatch } from 'react-redux';
import { formatStartOfDay, formatEndOfDay, formatDate } from '../utils/visitasDateFilters';

const useTableTabs = ({
    ENDPOINTS_BY_TAB,
    filters,
    extraFilters
}: {
    ENDPOINTS_BY_TAB: any[];
    filters: any;
    extraFilters: any;
}) => {
    const dispatch = useDispatch();
    const currentTabIndex = useAppSelector((state) => state.visits.currentTab);

    const [getData, result] =
        typeof ENDPOINTS_BY_TAB[currentTabIndex] === 'function'
            ? ENDPOINTS_BY_TAB[currentTabIndex]()
            : [() => Promise.resolve(null), { isLoading: false, error: 'Invalid tab function' }];
    const getDataRef = useRef(getData);

    useEffect(() => {
        getDataRef.current = getData;
    }, [getData]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        dispatch(setCurrentTab(newValue));
    };

    useEffect(() => {
        const fetch = async () => {
            try {
                //makethe default value of extraFilters is {} in case of null
                const currentExtraFilters = extraFilters || {};

                let mergedFilters = {
                    ...filters,
                    ...currentExtraFilters,
                    ordenador: filters?.ordenador ?? currentExtraFilters?.ordenador ?? 'id:asc'
                };

                // Provincia y Localidad vienen como números, no objetos
                if (currentExtraFilters.idProvincia !== undefined) {
                    mergedFilters.idProvincia = currentExtraFilters.idProvincia; // número directo
                }

                if (currentExtraFilters.idLocalidad !== undefined) {
                    mergedFilters.idLocalidad = currentExtraFilters.idLocalidad; // número directo
                }

                // Estado especial: reemplaza idsEstado
                if (currentExtraFilters.estado !== undefined) {
                    mergedFilters.estado = currentExtraFilters.estado; // número directo
                    delete mergedFilters.idsEstado;
                }
                if (
                    currentExtraFilters.idTipoVisita === '' ||
                    currentExtraFilters.idTipoVisita === null ||
                    currentExtraFilters.idTipoVisita === undefined
                ) {
                    delete mergedFilters.idTipoVisita;
                }

                // Preventor: agregar idPreventor sin borrar otros filtros
                if (currentExtraFilters.preventor !== undefined) {
                    mergedFilters.idPreventor = currentExtraFilters.preventor;
                }

                // Fecha de Visita, desde y hasta: se guardan como valor crudo del DatePicker
                // (dayjs) y se normalizan recien aca. Si el usuario todavia esta tipeando una
                // fecha incompleta, se descarta del query en vez de romper el pedido.
                if (currentExtraFilters.fechaVisita !== undefined) {
                    const fechaVisita = formatDate(currentExtraFilters.fechaVisita);
                    if (fechaVisita) {
                        mergedFilters.fechaVisita = fechaVisita;
                    } else {
                        delete mergedFilters.fechaVisita;
                    }
                }

                if (currentExtraFilters.fechaInicio !== undefined) {
                    const fechaInicio = formatStartOfDay(currentExtraFilters.fechaInicio);
                    if (fechaInicio) {
                        mergedFilters.fechaInicio = fechaInicio;
                    } else {
                        delete mergedFilters.fechaInicio;
                    }
                }

                if (currentExtraFilters.fechaFin !== undefined) {
                    const fechaFin = formatEndOfDay(currentExtraFilters.fechaFin);
                    if (fechaFin) {
                        mergedFilters.fechaFin = fechaFin;
                    } else {
                        delete mergedFilters.fechaFin;
                    }
                }

                await getDataRef.current(mergedFilters).unwrap();
            } catch (error) {
                console.error('Error fetching data for tab:', error);
            }
        };
        fetch();
    }, [currentTabIndex, filters, extraFilters]);

    return {
        currentTabIndex,
        handleTabChange,
        result,
        getData,
        fetchedData: result?.data
    };
};

export default useTableTabs;
